import { Component, Element, Host, h, Prop, State, Watch } from '@stencil/core';
import { ChevronRight } from 'src/icons';
import { addDataLayer, xssFilter } from '@ugc-utils/utils';

@Component({
  tag: 'ugc-ellipsis-text',
  shadow: false,
})
export class UgcEllipsisText {
  @Element() el: HTMLElement;

  @Prop() line: number = 3;
  @Prop() text: string = '';
  @Prop() url?: string = '';
  @Prop() images = [];
  @Prop() handleImageClick: (index: number) => void;

  @Prop() readMore: string = 'Show more';
  @Prop() readLess: string = 'Show less';

  @Prop() contentStyle = {};
  @Prop() buttonStyle = {};
  @Prop() buttonGap = {};

  @Prop() iconSize = '24';

  @Prop() dataLayerAttr?: {
    event: string;
    parameters: {
      interaction_type: string;
      location_detail: string;
    };
  };

  @State() isOpen: boolean = false;
  @State() isShowReadMore: boolean = false;
  height = 24;
  private intersectionObserver: IntersectionObserver;

  getHeight = (): number => {
    return this.el.getBoundingClientRect().height;
  };

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  tracking = () => {
    if (this.dataLayerAttr) {
      if (this.isOpen) {
        addDataLayer(this.dataLayerAttr);
      }
    }
  };

  setIsShowReadMore = () => {
    this.isShowReadMore = this.getHeight() > this.line * this.height || !!this.images.length;
  };

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  componentDidLoad() {
    this.intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.setIsShowReadMore();
          this.disconnectedCallback();
        }
      });
    });

    this.intersectionObserver.observe(this.el);
  }

  @Watch('text')
  setText() {
    this.isOpen = false;
    this.isShowReadMore = false;
    this.setIsShowReadMore();
  }

  render() {
    if (!this.text) return null;
    return (
      <Host>
        <div class="flex-col-start-start">
          <ugc-with-link url={this.url}>
            <p
              class={`fs-16 mb-0 color-gray-700 break-word ${
                this.isShowReadMore && !this.isOpen ? `ellipsis-text-${this.line}` : ''
              }`}
              style={this.contentStyle}
              innerHTML={xssFilter(this.text)}
            />
          </ugc-with-link>

          {this.isOpen && !!this.images.length && (
            <div class="mt-8 flex-row-start-start flex-wrap gap-4">
              {this.images.map(
                (item, index) =>
                  item.paths &&
                  item.paths.m && (
                    <ugc-image
                      imageUrl={item.paths.m}
                      size={105}
                      key={item.assetId}
                      onClick={() => this.handleImageClick(index)}
                    />
                  )
              )}
            </div>
          )}

          {this.isShowReadMore && (
            <div
              class="flex-row-start-center"
              onClick={() => {
                this.toggle();
                this.tracking();
              }}
              style={this.buttonGap}
            >
              <span class="fs-16 pointer color-blue-dark" style={this.buttonStyle}>
                {this.isOpen ? this.readLess : this.readMore}
              </span>
              <span
                class="flex-row-start-start pointer"
                style={{ transform: this.isOpen ? 'rotate(270deg)' : 'rotate(90deg)' }}
              >
                <ChevronRight width={this.iconSize} height={this.iconSize} fill="#126cc5" />
              </span>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
